import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorMessage, ModalCourseDetailWrapper } from '../styled';
import { DatePicker, Spin } from 'antd';
import ModalCommon from 'components/Input/ModalCommon';
import Input from 'components/Input/Input';
import Select from 'components/Select';
import Button from 'components/Button';
import images, { toolbarOptions3 } from 'assets/sheets/images';
import ChooseOnlyTime from 'components/ChooseDate/ChooseOnlyTime';
import dayjs from 'dayjs';
import CourseServices from 'services/CourseServices';
import { validateForm } from 'helper/validation';
import { getDateMinus, isScheduleValid, mergeErrors } from 'helper/utils';
import CourseScheduleServices from 'services/CourseScheduleServices';
import { toast } from 'sonner';
import Editor from 'components/Editor/Editor';
import { NO_INTERNET } from 'components/messageError';
import { clearEditable, initData } from 'components/Editor/special-action';

const initialState = {
  title: '',
  course: '',
  region: '',
  location: '',
  newCapacity: '',
  retakeCapacity: '',
  minimumParticipants: '',
  instructor: '',
  status: '',
  mapUrl: '',
  note: '',
  deadlineNew: '',
  deadline: [],
  registrations: [],
  totalRegistrations: 0,
  totalNewStudents: 0,
  totalRetakeStudents: 0,
  isHappened: false,
};

export const areas = [
  { label: '北海道・東北エリア', value: '北海道・東北エリア' },
  { label: '関東エリア', value: '関東エリア' },
  { label: '北信越・中部エリア', value: '北信越・中部エリア' },
  { label: '近畿エリア', value: '近畿エリア' },
  { label: '中国・四国エリア', value: '中国・四国エリア' },
  { label: '九州・沖縄エリア', value: '九州・沖縄エリア' },
];

const ModalCourse = ({ isOpen, onClose, onSuccessEdit, isEdit, onSuccess, courseId }) => {
  const reportRef = useRef(null);
  const modalRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...initialState });
  const [errors, setErrors] = useState([]);
  const [resetKey, setResetKey] = useState(0);

  // Reset errors when modal is opened/closed
  useEffect(() => {
    if (isOpen) {
      // Clear all errors when opening the modal
      setErrors([]);
    }

    return () => {
      // Cleanup errors when closing the modal
      setErrors([]);
    };
  }, [isOpen]);

  useEffect(() => {
    setResetKey((k) => k + 1);
  }, [isOpen]);

  const [courses, setCourses] = useState([]);
  const [datePickerOpen, setDatePickerOpen] = useState({
    id: null,
    open: false,
  });

  // Close date picker on scroll with debounce
  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      // Clear any existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Set a new timeout
      scrollTimeout = setTimeout(() => {
        setDatePickerOpen((prev) => (prev.open ? { ...prev, open: false } : prev));
      }, 50); // 100ms debounce time
    };

    // Use passive event listener for better performance
    const options = { passive: true, capture: true };

    // Add the event listener
    document.addEventListener('scroll', handleScroll, options);

    // Cleanup function
    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      document.removeEventListener('scroll', handleScroll, options);
    };
  }, []);

  const handleDatePickerOpen = (id, open) => {
    setDatePickerOpen({
      id,
      open,
    });
  };
  const [regions, setRegions] = useState([]);
  const [scheduleEntries, setScheduleEntries] = useState([
    {
      id: 0,
      date: '',
      start: '',
      end: '',
      retakeDeadline: '',
    },
  ]);

  const handleAfterOpen = useCallback(() => {
    const modalBody = document.querySelector('.ant-modal-body');
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
  }, []);

  const handleCheckScheduleHappened = (schedules = []) => {
    if (!Array.isArray(schedules) || schedules.length === 0) return false;

    const currentTime = dayjs();

    return schedules.some((schedule) => {
      const startTime = dayjs(schedule.startTime);
      return currentTime.isAfter(startTime);
    });
  };

  const isHaveStudentRegister = useMemo(() => form.totalRegistrations > 0, [form]);

  const handleCheckDeleteDaySchedule = (data = []) => {
    if (!Array.isArray(data) || data.length === 0) return false;

    const now = dayjs();

    return data.some((item) => {
      if (!item.schedule || item.schedule.length === 0) return false;

      return item.schedule.some((schedule) => {
        const start = dayjs(schedule.startTime);

        return now.isAfter(start) || now.isSame(start);
      });
    });
  };

  useEffect(() => {
    handleCheckDeleteDaySchedule(scheduleEntries);
  }, [form]);

  const getRegions = () =>
    CourseScheduleServices.getRegions()
      .then((res) => setRegions(res?.data.map((item) => ({ label: item, value: item }))))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => {
    getRegions();
  }, []);

  const getScheduleById = () =>
    CourseScheduleServices.getDetail(courseId)
      .then((res) => {
        const isHappened = handleCheckScheduleHappened(res?.data.schedule);
        setForm({
          ...res?.data,
          deadlineNew: res?.data.deadlineNew
            ? dayjs(res?.data.deadlineNew).format('YYYY/MM/DD')
            : '',
          deadlineRepeat: res?.data.deadlineRepeat
            ? dayjs(res?.data.deadlineRepeat).format('YYYY/MM/DD')
            : '',
          minimumParticipants: res?.data.minimumParticipants || '',
          newCapacity: res?.data.newCapacity || '',
          retakeCapacity: res?.data.retakeCapacity || '',
          instructor: res?.data.instructor || '',
          location: res?.data.location || '',
          mapUrl: res?.data.mapUrl || '',
          note: clearEditable(res?.data.note || ''),
          course: res?.data.course || '',
          region: res?.data.region || '',
          isHappened,
        });
        setScheduleEntries(
          res?.data.schedule.map((item, index) => ({
            id: index,
            date: dayjs(item.startTime).format('YYYY/MM/DD'),
            start: dayjs(item.startTime),
            end: dayjs(item.endTime),
            retakeDeadline: item.dayOfRetake,
          })),
        );

        // Reinit snapshot after data loaded
        setTimeout(() => {
          reportRef.current?.reinitSnapshot();
        }, 100);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => {
    if (isOpen && isEdit && courseId) {
      getScheduleById();
    }
  }, [isOpen, isEdit, courseId]);

  const updateSchedule = () => {
    setLoading(true);
    const bodyData = {
      schedule: [
        ...scheduleEntries.map((entry) => ({
          startTime: dayjs(entry.start).toISOString(),
          endTime: dayjs(entry.end).toISOString(),
          dayOfRetake: dayjs(entry.retakeDeadline).toISOString(),
        })),
      ],
      course: form.course,
      instructor: form.instructor,
      region: form.region,
      location: form.location,
      mapUrl: form.mapUrl,
      newCapacity: Number(form.newCapacity),
      retakeCapacity: Number(form.retakeCapacity) || 0,
      minimumParticipants: Number(form.minimumParticipants) || 0,
      note: initData(form.note),
      deadlineNew: form.deadlineNew,
    };

    if (form.newCapacity < form.totalNewStudents) {
      toast.error('申込数が定員を超えています');
      setLoading(false);
      return;
    }

    if (form.retakeCapacity < form.maxReturnStudents) {
      toast.error('申込数が定員を超えています');
      setLoading(false);
      return;
    }

    CourseScheduleServices.updateCourse(courseId, bodyData)
      .then(() => {
        toast.success('養成コースが更新されました。');
        onClose();
        onSuccessEdit();
        setErrors([]);
        setForm({ ...initialState });
        setScheduleEntries([
          {
            id: 0,
            date: '',
            start: '',
            end: '',
            retakeDeadline: '',
          },
        ]);
      })
      .catch((err) => {
        toast.error(err || err?.message || '処理に失敗しました。もう一度お試しください。');
      })
      .finally(() => setLoading(false));
  };

  const validations = [
    {
      label: 'course',
      value: form.course,
      rules: [
        {
          required: true,
          message: 'コース名を選択してください。',
        },
      ],
    },
    {
      label: 'region',
      value: form.region,
      rules: [
        {
          required: true,
          message: '開催エリアを選択してください。',
        },
      ],
    },
    {
      label: 'location',
      value: form.location,
      rules: [
        {
          required: true,
          message: '開催地を入力してください。',
        },
      ],
    },
    {
      label: 'newCapacity',
      value: form.newCapacity,
      rules: [
        {
          required: true,
          message: '定員を入力してください。',
        },
        {
          regex: /^[0-9]+$/,
          message: '定員を正しく入力してください。',
        },
      ],
    },
    {
      label: 'retakeCapacity',
      value: form.retakeCapacity,
      rules: [
        {
          regex: /^[0-9]+$/,
          message: '定員を正しく入力してください。',
        },
      ],
    },
    {
      label: 'minimumParticipants',
      value: form.minimumParticipants,
      rules: [
        {
          regex: /^[0-9]+$/,
          message: '最少催行人数を正しく入力してください。',
        },
      ],
    },
    {
      label: 'instructor',
      value: form.instructor,
      rules: [
        {
          required: true,
          message: '講師を入力してください。',
        },
      ],
    },
    {
      label: 'mapUrl',
      value: form.mapUrl,
      rules: [
        {
          regex: /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/,
          message: '有効なURLを入力してください',
        },
      ],
    },
    {
      label: 'deadlineNew',
      value: form.deadlineNew,
      rules: [
        {
          required: true,
          message: '申込期限を入力してください。',
        },
      ],
    },
  ];

  const updateDeadline = (index, date) => {
    setScheduleEntries((prevEntries) => {
      // Ensure prevEntries is an array
      if (!Array.isArray(prevEntries)) {
        return [];
      }

      // Update the retakeDeadline for the specific entry
      return prevEntries.map((entry, i) =>
        i === index
          ? {
              ...entry,
              retakeDeadline: date ? dayjs(date).format('YYYY/MM/DD') : '',
            }
          : entry,
      );
    });
  };

  const getCourse = () =>
    CourseServices.getCourses()
      .then((res) => {
        setCourses(res?.data || []);
      })
      .catch((err) => toast.error(err?.message || err || NO_INTERNET))
      .finally(() => setLoading(false));

  useEffect(() => {
    getCourse();
  }, [isOpen]);

  const handleAddEntry = () => {
    if (scheduleEntries.length < 8)
      setScheduleEntries((prev) => [
        ...prev,
        {
          id: prev.length,
          date: '',
          start: '',
          end: '',
          deadlineNew: '',
          deadlineRepeat: '',
        },
      ]);
  };

  const handleRemoveEntry = (id) => {
    setScheduleEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const updateEntry = useCallback(
    (id, key, value) => {
      // Clear all errors for this entry first
      setErrors((prev) =>
        prev.filter((error) => !(error.id === id && error.label === 'dateTimeEntries')),
      );

      // Handle date updates
      if (key === 'date') {
        setScheduleEntries((prevEntries) => {
          const entryIndex = prevEntries.findIndex((e) => e.id === id);
          if (entryIndex === -1) return prevEntries;

          const entry = prevEntries[entryIndex];
          const newDate = value ? dayjs(value).format('YYYY/MM/DD') : '';
          const updates = {
            date: newDate,
            retakeDeadline: newDate ? getDateMinus(newDate, 3) : '',
          };

          // Only validate if we have a new date
          if (newDate) {
            const currDate = dayjs(newDate, 'YYYY/MM/DD');
            const prevEntry = entryIndex > 0 ? prevEntries[entryIndex - 1] : null;
            let errorMessage = null;

            // Check if date is before or same as previous date
            if (prevEntry?.date) {
              const prevDate = dayjs(prevEntry.date, 'YYYY/MM/DD');
              if (currDate.isSame(prevDate) || currDate.isBefore(prevDate)) {
                errorMessage = '開催日時を正しく入力してください。';
              }
            }

            // Update start/end times with new date if they exist
            if (entry.start) {
              const startTime = dayjs(entry.start).format('HH:mm');
              updates.start = dayjs(`${newDate} ${startTime}`, 'YYYY/MM/DD HH:mm');
            }
            if (entry.end) {
              const endTime = dayjs(entry.end).format('HH:mm');
              updates.end = dayjs(`${newDate} ${endTime}`, 'YYYY/MM/DD HH:mm');
            }

            if (!entry.start || !entry.end) {
              errorMessage = '開催日時を入力してください。';
            }

            // Add error if validation failed
            if (errorMessage) {
              setErrors((prev) => [
                ...prev.filter((e) => !(e.id === id && e.label === 'dateTimeEntries')),
                {
                  label: 'dateTimeEntries',
                  id: id,
                  type: 'date',
                  message: errorMessage,
                },
              ]);
            }
          }

          // Update the entries
          const updatedEntries = prevEntries.map((entry) =>
            entry.id === id ? { ...entry, ...updates } : entry,
          );

          // Update form state
          setForm((prev) => ({
            ...prev,
            dateTimeEntries: updatedEntries.map((entry) => ({
              ...entry,
              retakeDeadline:
                entry.retakeDeadline || (entry.date ? getDateMinus(entry.date, 3) : ''),
            })),
          }));

          hasSetDeadlines.current = false;
          return updatedEntries;
        });
        return;
      }

      // Handle time updates (start/end)
      if ((key === 'start' || key === 'end') && value !== undefined) {
        setScheduleEntries((prevEntries) => {
          const entry = prevEntries.find((e) => e.id === id);
          if (!entry) return prevEntries;

          let updatedValue = value;
          // If we have a date, combine it with the time
          if (entry.date && value) {
            const timeValue = dayjs(value).format('HH:mm');
            updatedValue = dayjs(`${entry.date} ${timeValue}`, 'YYYY/MM/DD HH:mm');
          }

          const updates = { [key]: updatedValue };
          const otherKey = key === 'start' ? 'end' : 'start';
          const otherValue = entry[otherKey];

          // Only validate if we have both times
          if (updatedValue && otherValue) {
            const isStartBeforeEnd =
              key === 'start'
                ? dayjs(updatedValue).isBefore(dayjs(otherValue))
                : dayjs(otherValue).isBefore(dayjs(updatedValue));

            if (!isStartBeforeEnd) {
              setErrors((prev) => [
                ...prev.filter((e) => !(e.id === id && e.label === 'dateTimeEntries')),
                {
                  label: 'dateTimeEntries',
                  id: id,
                  type: 'time',
                  message: '開催日時を正しく入力してください。',
                },
              ]);
            }
          }

          // Update the entries
          const updatedEntries = prevEntries.map((entry) =>
            entry.id === id ? { ...entry, ...updates } : entry,
          );

          // Update form state
          setForm((prev) => ({
            ...prev,
            dateTimeEntries: updatedEntries.map((entry) => ({
              ...entry,
              retakeDeadline:
                entry.retakeDeadline || (entry.date ? getDateMinus(entry.date, 3) : ''),
            })),
          }));

          return updatedEntries;
        });
        return;
      }

      // Handle clearing values
      if ((key === 'start' || key === 'end' || key === 'date') && value === null) {
        setScheduleEntries((prevEntries) =>
          prevEntries.map((entry) => (entry.id === id ? { ...entry, [key]: '' } : entry)),
        );
      }
    },
    [getDateMinus],
  );

  const hasSetDeadlines = useRef(false);

  useEffect(() => {
    if (!hasSetDeadlines.current && scheduleEntries[0].id === 0 && scheduleEntries[0].date) {
      hasSetDeadlines.current = true;
      const dateMinus7 = getDateMinus(scheduleEntries[0].date, 7);
      setForm((prev) => ({
        ...prev,
        deadlineNew: dateMinus7,
      }));
    }
  }, [scheduleEntries[0]]);

  const handleAddCourseSchedule = () => {
    setLoading(true);
    const bodyData = {
      schedule: [
        ...scheduleEntries.map((entry) => ({
          startTime: dayjs(entry.start).toISOString(),
          endTime: dayjs(entry.end).toISOString(),
          dayOfRetake: dayjs(entry.retakeDeadline).toISOString(),
          alphanumericCharacters: null,
        })),
      ],
      course: form.course,
      instructor: form.instructor,
      region: form.region,
      location: form.location,
      mapUrl: form.mapUrl,
      newCapacity: Number(form.newCapacity),
      retakeCapacity: Number(form.retakeCapacity) || 0,
      minimumParticipants: Number(form.minimumParticipants) || 0,
      note: clearEditable(form.note),
      deadlineNew: form.deadlineNew,
    };

    CourseScheduleServices.createCourseSchedule({
      ...bodyData,
    })
      .then((res) => {
        if (res.isOk) {
          toast.success('新しい養成コースを登録しました。');
          onClose();
          onSuccess();
          setForm(initialState);
          setErrors([]);
          setScheduleEntries([
            {
              id: 0,
              date: '',
              start: '',
              end: '',
              retakeDeadline: '',
            },
          ]);
        }
      })
      .catch((err) => {
        toast.error(err?.message || '処理に失敗しました。もう一度お試しください。');
      })
      .finally(() => setLoading(false));
  };

  const checkInvalidDatesDeadLine = (entries) => {
    const errs = [];
    for (let i = 1; i < entries.length; i++) {
      // const prev = dayjs(entries[i - 1].retakeDeadline);
      // const curr = dayjs(entries[i].retakeDeadline);
      // if (curr.isBefore(prev)) {
      //   errs.push({
      //     id: i,
      //     label: 'retakeDeadline',
      //     type: 'date',
      //     message: '申込期限を正しく入力してください。',
      //   });
      //   setErrors((prev) => [
      //     ...prev,
      //     {
      //       id: i,
      //       label: 'retakeDeadline',
      //       type: 'date',
      //       message: '申込期限を正しく入力してください。',
      //     },
      //   ]);
      // }
    }
    return errs;
  };

  const checkInvalidDates = (entries) => {
    const errs = [];
    for (let i = 1; i < entries.length; i++) {
      // const prev = dayjs(entries[i - 1].retakeDeadline);
      // const curr = dayjs(entries[i].retakeDeadline);
      // if (curr.isBefore(prev)) {
      //   errs.push({
      //     id: i,
      //     label: 'retakeDeadline',
      //     type: 'date',
      //     message: '申込期限を正しく入力してください。',
      //   });
      //   setErrors((prev) => [
      //     ...prev,
      //     {
      //       id: i,
      //       label: 'retakeDeadline',
      //       type: 'date',
      //       message: '申込期限を正しく入力してください。',
      //     },
      //   ]);
      // }
    }
    return errs;
  };

  const handleCheckTimeRetake = () => {
    let errs = [];
    scheduleEntries.map((item, i) => {
      if (!item.retakeDeadline || item.retakeDeadline === '') {
        errs.push({
          id: i,
          label: 'retakeDeadline',
          type: 'date',
          message: '申込期限を入力してください。',
        });
      }
      if (dayjs(item.retakeDeadline).isAfter(dayjs(item.date))) {
        errs.push({
          id: i,
          label: 'retakeDeadline',
          type: 'date',
          message: '申込期限を正しく入力してください。',
        });
      }
    });

    setErrors((prev) => [...prev, ...errs]);
    return errs;
  };

  const handleSubmit = () => {
    if (loading) return;
    const formErrors = validateForm(form, validations, (errors) => setErrors(errors));

    const scheduleValidation = scheduleEntries.map((entry) => {
      try {
        const prevEntry = scheduleEntries[entry.id - 1];
        const { isValid, ...result } = isScheduleValid(entry, prevEntry);
        return !isValid ? result : null;
      } catch {
        return null;
      }
    });

    const errsSchedule = scheduleValidation.filter(Boolean);
    const errsDeadLine = checkInvalidDatesDeadLine(scheduleEntries);
    const errsDate = checkInvalidDates(scheduleEntries);
    const errsRetake = handleCheckTimeRetake(scheduleEntries);
    const errsDeadlineNew = handleCheckDeadlineNew();

    const allErrors = mergeErrors(
      Object.values(formErrors),
      errsSchedule,
      errsDeadLine,
      errsDate,
      errsRetake,
      errsDeadlineNew,
    );

    if (allErrors.length > 0) {
      setErrors(allErrors);
      return;
    }
    if (courseId) updateSchedule();
    else handleAddCourseSchedule();
  };

  const handleChangeData = ({ label, value }) => {
    setErrors((prev) => {
      const idx = prev.findIndex((e) => e.label === label);
      return idx !== -1 ? prev.filter((_, i) => i !== idx) : prev;
    });
    setForm({
      ...form,
      [label]: value,
    });

    if (label === 'applicationDeadline.new') {
      setForm({
        ...form,
        applicationDeadline: {
          ...form.applicationDeadline,
          new: value,
        },
      });
    }
  };

  const handleFieldErrorChange = (label, message) => {
    setErrors((prev) => {
      const idx = prev.findIndex((e) => e.label === label);
      if (message) {
        return idx !== -1
          ? prev.map((e, i) => (i === idx ? { label, message } : e))
          : [...prev, { label, message }];
      } else {
        return idx !== -1 ? prev.filter((_, i) => i !== idx) : prev;
      }
    });
  };

  const handleCheckDeadlineNew = () => {
    var errs = [];

    if (!form.deadlineNew || form.deadlineNew === '') {
      errs.push({
        label: 'deadlineNew',
        message: '申込期限を入力してください。',
      });
    } else if (dayjs(form.deadlineNew).isAfter(dayjs(scheduleEntries[0].date))) {
      errs.push({
        label: 'deadlineNew',
        message: '申込期限を正しく入力してください。',
      });
    }
    setErrors((prev) => [...prev, ...errs]);
    return errs;
  };

  return (
    <ModalCourseDetailWrapper>
      <div ref={modalRef}>
        <Spin spinning={loading}>
          <ModalCommon
            width={700}
            heightHeader={56}
            title={!isEdit ? '新規追加' : '開催編集'}
            open={isOpen}
            padding={[24, 24, 24, 24]}
            onAfterOpen={handleAfterOpen}
            onCancel={() => {
              setErrors([]);
              setForm(initialState);
              setScheduleEntries([
                {
                  id: 0,
                  date: '',
                  start: '',
                  end: '',
                  deadlineNew: '',
                  deadlineRepeat: '',
                },
              ]);
              reportRef.current?.reset();
              onClose();
            }}
            onSubmit={onSuccess}
            footer={
              <div className="flex h-max justify-end gap-4 border-t border-[#e9e9e9] pt-4">
                <Button
                  btnType="back"
                  onClick={() => {
                    setErrors([]);
                    setForm(initialState);
                    setScheduleEntries([
                      {
                        id: 0,
                        date: '',
                        start: '',
                        end: '',
                        deadlineNew: '',
                        deadlineRepeat: '',
                      },
                    ]);
                    reportRef.current?.reset();
                    onClose();
                  }}
                  height={40}
                  width={120}
                  fontSize={14}
                  className="border border-[#E6EBF1] bg-transparent text-[#51191199]"
                >
                  キャンセル
                </Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  fontSize={14}
                  loading={loading}
                  height={40}
                  width={120}
                >
                  <p style={{ letterSpacing: 0 }}>保存</p>
                </Button>
              </div>
            }
          >
            {isEdit && (
              <Input
                title="id"
                $heightInput={40}
                widthLabel={166}
                widthInput={460}
                label="開催ID"
                required
                value={form.eventId ?? ''}
                disabled={!!courseId}
                $paddingMessage={180}
              />
            )}
            <Select
              title="course"
              label="コース名"
              disabled={!!courseId}
              required
              options={courses
                .map((item) => ({
                  value: item._id,
                  label: item.name,
                }))
                .map((item) =>
                  item.label === 'インテグレーティッドトレーニング'
                    ? {
                        value: item.value,
                        label: 'インテグレーティッド',
                      }
                    : item,
                )}
              $autoTrim={false}
              $heightInput={40}
              widthLabel={166}
              $widthLabelPopup={166}
              widthSelect={460}
              value={form.course ?? ''}
              onChange={(e) => handleChangeData({ label: 'course', value: e.value })}
              // disabled={!!courseId}
              $onErrorChange={handleFieldErrorChange}
              errors={errors}
              $paddingMessage={180}
            />

            <Select
              title="region"
              label="開催エリア"
              required
              disabled={!!courseId}
              $autoTrim={false}
              heightSelect={40}
              widthLabel={166}
              $widthLabelPopup={166}
              widthSelect={460}
              value={form.region ?? ''}
              onChange={(e) => handleChangeData({ label: 'region', value: e.value })}
              $onErrorChange={handleFieldErrorChange}
              options={regions || []}
              errors={errors}
              $paddingMessage={180}
            />

            <Input
              title="location"
              label="開催地"
              required
              widthInput={460}
              $autoTrim={false}
              disabled={!!courseId && form.isHappened}
              rules={[
                {
                  required: true,
                  message: '開催地を入力してください。',
                },
                {
                  validator: (_, value) => {
                    if (!value || value === '') {
                      return Promise.reject('有料コースの場合は、料金を入力してください。');
                    }
                    return Promise.resolve();
                  },
                  validateTrigger: 'onChange',
                },
              ]}
              $heightInput={40}
              widthLabel={166}
              value={form.location ?? ''}
              onChange={(e) => handleChangeData({ label: 'location', value: e })}
              $onErrorChange={handleFieldErrorChange}
              errors={errors}
              $paddingMessage={180}
            />

            <div className="flex gap-4">
              <label htmlFor="capacity" className="flex w-[166px] gap-4 text-[14px]">
                定員
                <span className="text-[#FF0000]">*</span>
              </label>

              <div className="flex flex-1 flex-col gap-6 lg:gap-4">
                <div className="flex flex-col gap-4">
                  <label htmlFor="capacityNew">新規</label>
                  <Input
                    title="newCapacity"
                    required
                    $autoTrim={false}
                    disabled={!!courseId && form.isHappened}
                    $heightInput={40}
                    widthLabel={166}
                    widthInput={460}
                    rules={[
                      {
                        required: true,
                        message: '定員を入力してください。',
                      },
                      {
                        validator: (_, value) => {
                          if (!value || value === '') {
                            return Promise.reject('定員を入力してください。');
                          }
                          if (!/^[0-9]+$/.test(value) || Number(value) < 0)
                            return Promise.reject('定員を正しく入力してください。');
                        },
                        validateTrigger: 'onChange',
                      },
                    ]}
                    value={form.newCapacity ?? ''}
                    onChange={(e) => handleChangeData({ label: 'newCapacity', value: e })}
                    $onErrorChange={handleFieldErrorChange}
                    errors={errors}
                    $paddingMessage={0}
                  />
                </div>

                <div className="flex w-full flex-1 flex-col gap-4">
                  <label htmlFor="capacityRepeat">再受講</label>
                  <Input
                    title="retakeCapacity"
                    required
                    $autoTrim={false}
                    disabled={!!courseId && form.isHappened}
                    $heightInput={40}
                    widthLabel={166}
                    widthInput={460}
                    value={form.retakeCapacity ?? ''}
                    $paddingMessage={0}
                    rules={[
                      {
                        validator: (_, value) => {
                          if ((value !== '' && !/^[0-9]+$/.test(value)) || Number(value) < 0)
                            return Promise.reject('定員を正しく入力してください。');
                        },
                        validateTrigger: 'onChange',
                      },
                    ]}
                    onChange={(e) => handleChangeData({ label: 'retakeCapacity', value: e })}
                    $onErrorChange={handleFieldErrorChange}
                    errors={errors}
                  />
                </div>
              </div>
            </div>

            <Input
              title="minimumParticipants"
              label="最少催行人数"
              $autoTrim={false}
              $heightInput={40}
              widthLabel={166}
              disabled={!!courseId && form.isHappened}
              widthInput={460}
              rules={[
                {
                  validator: (_, value) => {
                    if (value && value !== 0 && !/^[0-9]+$/.test(value))
                      return Promise.reject('最少催行人数を正しく入力してください。');
                  },
                  validateTrigger: 'onChange',
                },
              ]}
              value={form.minimumParticipants || ''}
              onChange={(e) => handleChangeData({ label: 'minimumParticipants', value: e })}
              $onErrorChange={handleFieldErrorChange}
              errors={errors}
              $paddingMessage={180}
            />

            <Input
              title="instructor"
              label="講師"
              required
              $autoTrim={false}
              $heightInput={40}
              disabled={!!courseId && form.isHappened}
              widthLabel={166}
              widthInput={460}
              value={form.instructor ?? ''}
              rules={[
                {
                  required: true,
                  message: '講師を入力してください。',
                },
                {
                  validator: (_, value) => {
                    if (!value || value === '') {
                      return Promise.reject('講師を入力してください。');
                    }
                    return Promise.resolve();
                  },
                  validateTrigger: 'onChange',
                },
              ]}
              onChange={(e) => handleChangeData({ label: 'instructor', value: e })}
              $onErrorChange={handleFieldErrorChange}
              errors={errors}
              $paddingMessage={180}
            />

            <Input
              title="mapUrl"
              label="グーグルMAP のリンク追加"
              $autoTrim={false}
              $maxWidthLabel={100}
              $heightInput={40}
              disabled={!!courseId && form.isHappened}
              classNameLabel="w-[100px]"
              classNameRow="!gap-[80px]"
              widthInput={460}
              value={form.mapUrl ?? ''}
              rules={[
                {
                  validator: (_, value) => {
                    if (value.length < 1) {
                      return Promise.resolve();
                    }
                    // If value is empty string or only whitespace, don't show error
                    if (value.length > 0 && value.trim() === '') {
                      return Promise.reject('有効なURLを入力してください');
                    }

                    // Check if it's a valid URL (only if not empty or whitespace)
                    if (!/^https?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/\S*)?$/.test(value)) {
                      return Promise.reject('有効なURLを入力してください');
                    }

                    return Promise.resolve();
                  },
                  validateTrigger: 'onChange',
                },
              ]}
              onChange={(e) => handleChangeData({ label: 'mapUrl', value: e })}
              $onErrorChange={handleFieldErrorChange}
              errors={errors}
              $paddingMessage={180}
            />

            <Editor
              ref={reportRef}
              label={'備考'}
              title="note"
              alignLabel="center"
              widthLabel={166}
              options={[toolbarOptions3]}
              widthEditor={460}
              heightEditor={200}
              value={form.note ?? ''}
              resetKey={resetKey}
              onChange={handleChangeData}
            />

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <span className="flex w-[166px] items-center font-medium">
                  開催日時
                  <span className="ml-1 text-red-500">*</span>
                </span>
                <div className="group">
                  <Button
                    btnType={!!courseId && form.isHappened ? 'disabled' : 'outline'}
                    width={143}
                    height={32}
                    onClick={handleAddEntry}
                    disabled={scheduleEntries.length >= 8}
                    classNameChildren={`!flex !items-center !justify-center !gap-2 ${
                      scheduleEntries.length >= 8
                        ? 'opacity-50 border-none'
                        : 'group-hover:[&>svg]:brightness-0 group-hover:[&>svg]:invert'
                    }`}
                  >
                    <images.add.src />
                    日時を追加する
                  </Button>
                </div>
              </div>

              <div className="flex w-full flex-col justify-start gap-4 pl-[180px]">
                {scheduleEntries.map((entry, index) => (
                  <div key={entry.id} className="flex w-max flex-col gap-0">
                    <div className="flex items-center gap-4">
                      <span className="w-[89px]">{index + 1} 日目</span>
                      <div className="flex items-center gap-2">
                        <DatePicker
                          format="YYYY/MM/DD"
                          disabled={!!courseId && form.isHappened}
                          className={`${errors.filter((e) => e.label === 'dateTimeEntries' && e.id === +index).length > 0 ? 'border-[#ff4d4f] shadow-error' : ''}`}
                          placeholder="yyyy/mm/dd"
                          onOpenChange={(open) => {
                            requestAnimationFrame(() => {
                              window.dispatchEvent(new Event('resize'));
                            });
                            handleDatePickerOpen(open ? `dateTimeEntries-${entry.id}` : null, open);
                          }}
                          open={
                            datePickerOpen.id === `dateTimeEntries-${entry.id}` &&
                            datePickerOpen.open
                          }
                          placement="topLeft"
                          getPopupContainer={(trigger) => trigger.parentNode}
                          value={entry.date ? dayjs(entry.date) : undefined}
                          onChange={(date) => {
                            updateEntry(entry.id, 'date', date || '');
                            // Clear any existing date errors for this entry

                            setErrors((prev) =>
                              prev.filter(
                                (e) => !(e.id === entry.id && e.label === 'retakeDeadline'),
                              ),
                            );
                            setErrors((prev) =>
                              prev.filter(
                                (e) =>
                                  !(
                                    e.label === 'dateTimeEntries' &&
                                    e.id === entry.id &&
                                    e.type === 'date'
                                  ),
                              ),
                            );
                            // Add error if date is cleared
                            if (!date && entry.date) {
                              setErrors((prev) => [
                                ...prev,
                                {
                                  label: 'dateTimeEntries',
                                  id: entry.id,
                                  type: 'date',
                                  message: '開催日時を入力してください。',
                                },
                              ]);
                            }
                          }}
                          onClear={() => {
                            updateEntry(entry.id, 'date', '');
                            // Clear any existing date errors for this entry
                            setErrors((prev) =>
                              prev.filter(
                                (e) =>
                                  !(
                                    e.label === 'dateTimeEntries' &&
                                    e.id === entry.id &&
                                    e.type === 'date'
                                  ),
                              ),
                            );
                            // Add error if date is cleared
                            if (!entry.date) {
                              setErrors((prev) => [
                                ...prev,
                                {
                                  label: 'dateTimeEntries',
                                  id: entry.id,
                                  type: 'date',
                                  message: '開催日時を入力してください。',
                                },
                              ]);
                            }
                          }}
                        />
                        <ChooseOnlyTime
                          key={`time-${entry.id}`}
                          widthInput={191}
                          placeholder="hh-mm"
                          disabled={!!courseId && form.isHappened}
                          date={entry.date}
                          errors={errors}
                          index={index}
                          start={entry.start}
                          end={entry.end}
                          onChange={([startTime, endTime]) => {
                            // Clear all errors for this entry first
                            setErrors((prevErrors) =>
                              prevErrors.filter(
                                (error) =>
                                  !(
                                    error.id === entry.id &&
                                    (error.label === 'retakeDeadline' ||
                                      error.label === 'dateTimeEntries')
                                  ),
                              ),
                            );

                            // Update both times in a single state update
                            setScheduleEntries((prevEntries) => {
                              const entryIndex = prevEntries.findIndex((e) => e.id === entry.id);
                              if (entryIndex === -1) return prevEntries;

                              const currentEntry = prevEntries[entryIndex];
                              const updates = {};

                              // Process start time
                              if (startTime !== undefined) {
                                updates.start =
                                  startTime && dayjs(startTime).isValid()
                                    ? currentEntry.date
                                      ? dayjs(
                                          `${currentEntry.date} ${dayjs(startTime).format('HH:mm')}`,
                                          'YYYY/MM/DD HH:mm',
                                        )
                                      : startTime
                                    : '';
                              }

                              // Process end time
                              if (endTime !== undefined) {
                                updates.end =
                                  endTime && dayjs(endTime).isValid()
                                    ? currentEntry.date
                                      ? dayjs(
                                          `${currentEntry.date} ${dayjs(endTime).format('HH:mm')}`,
                                          'YYYY/MM/DD HH:mm',
                                        )
                                      : endTime
                                    : '';
                              }

                              // Validate times
                              const newErrors = [];

                              // Case 1: Both times are empty
                              if (!startTime && !endTime) {
                                newErrors.push({
                                  label: 'dateTimeEntries',
                                  id: entry.id,
                                  type: 'time',
                                  message: '開催日時を入力してください。',
                                });
                              }
                              // Case 2: Only one time is entered
                              else if (!startTime || !endTime) {
                                newErrors.push({
                                  label: 'dateTimeEntries',
                                  id: entry.id,
                                  type: 'time',
                                  message: '開催日時を入力してください。',
                                });
                              }
                              // Case 3: Start time is after or equal to end time
                              else if (
                                dayjs(startTime).isSame(dayjs(endTime)) ||
                                dayjs(startTime).isAfter(dayjs(endTime))
                              ) {
                                newErrors.push({
                                  label: 'dateTimeEntries',
                                  id: entry.id,
                                  type: 'time',
                                  message: '開催日時を正しく入力してください。',
                                });
                              }
                              // Case 4: Date is the same as previous entry
                              else if (
                                entryIndex > 0 &&
                                currentEntry.date === prevEntries[entryIndex - 1]?.date
                              ) {
                                newErrors.push({
                                  label: 'dateTimeEntries',
                                  id: entry.id,
                                  type: 'date',
                                  message: '開催日時を正しく入力してください。',
                                });
                              }
                              // Case 5: Date is before previous entry
                              else if (
                                entryIndex > 0 &&
                                currentEntry.date &&
                                prevEntries[entryIndex - 1]?.date &&
                                dayjs(currentEntry.date).isBefore(
                                  dayjs(prevEntries[entryIndex - 1].date),
                                )
                              ) {
                                newErrors.push({
                                  label: 'dateTimeEntries',
                                  id: entry.id,
                                  type: 'date',
                                  message: '開催日時を正しく入力してください。',
                                });
                              }

                              // Update errors if needed
                              if (newErrors.length > 0) {
                                setErrors((prev) => [
                                  ...prev.filter(
                                    (e) => !(e.id === entry.id && e.label === 'dateTimeEntries'),
                                  ),
                                  ...newErrors,
                                ]);
                              }

                              // Update the entry
                              return prevEntries.map((e) =>
                                e.id === entry.id ? { ...e, ...updates } : e,
                              );
                            });

                            // Update form state
                            setForm((prev) => ({
                              ...prev,
                              dateTimeEntries: scheduleEntries.map((e) =>
                                e.id === entry.id
                                  ? {
                                      ...e,
                                      start: startTime || e.start,
                                      end: endTime || e.end,
                                      retakeDeadline:
                                        e.retakeDeadline ||
                                        (e.date ? getDateMinus(e.date, 3) : null),
                                    }
                                  : e,
                              ),
                            }));
                          }}
                        />

                        {scheduleEntries.length > 1 && index === scheduleEntries.length - 1 && (
                          <images.minusCircle.src
                            style={
                              !courseId
                                ? { cursor: 'pointer' }
                                : form.isHappened || isHaveStudentRegister > 0
                                  ? { cursor: 'not-allowed', opacity: 0.5 }
                                  : { cursor: 'pointer' }
                            }
                            onClick={() => {
                              const isSamed = form.isHappened || isHaveStudentRegister > 0;

                              if (!courseId || (courseId && !isSamed)) {
                                setErrors((prev) =>
                                  prev.filter(
                                    (e) => !(e.label === 'dateTimeEntries' && e.id === entry.id),
                                  ),
                                );
                                handleRemoveEntry(entry.id);
                              }
                            }}
                            className="cursor-pointer"
                          />
                        )}
                      </div>
                    </div>
                    <ErrorMessage
                      $paddingMessage={115}
                      style={{
                        display: errors.some(
                          (e) => e.label === 'dateTimeEntries' && e.id === entry.id,
                        )
                          ? 'block'
                          : 'none',
                      }}
                    >
                      <span>
                        {errors
                          .filter(
                            (error) => error.label === 'dateTimeEntries' && error.id === entry.id,
                          )
                          .map((error, i) => (
                            <div key={i}>{error.message}</div>
                          ))}
                      </span>
                    </ErrorMessage>
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-start">
                <div className="flex items-start gap-4">
                  <span className="flex w-[166px] gap-1 text-center text-sm font-medium">
                    申込期限 <span className="text-red-500">*</span>
                  </span>
                  <div className="flex items-center gap-4">
                    <span className="w-[89px] text-sm">新規</span>
                    <div className="flex flex-1 items-center gap-2">
                      <DatePicker
                        format="YYYY/MM/DD"
                        placeholder="yyyy/mm/dd"
                        className={`h-10 w-[345px] text-sm ${errors.filter((e) => e.label === 'deadlineNew').length > 0 ? 'border-[#ff4d4f] shadow-error' : ''}`}
                        disabled={!!courseId && form.isHappened}
                        open={datePickerOpen.id === 'deadline-new' && datePickerOpen.open}
                        onOpenChange={(open) => {
                          handleDatePickerOpen(open ? 'deadline-new' : null, open);
                        }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        popupAlign={{
                          overflow: { adjustX: true, adjustY: true },
                        }}
                        value={form.deadlineNew ? dayjs(form.deadlineNew) : undefined}
                        onChange={(date) => {
                          handleChangeData({
                            label: 'deadlineNew',
                            value: date,
                          });
                          if (date) {
                            if (dayjs(scheduleEntries[0].date).isBefore(dayjs(date))) {
                              setErrors((prev) => {
                                return [
                                  ...prev,
                                  {
                                    label: 'deadlineNew',
                                    message: '申込期限を正しく入力してください。',
                                  },
                                ];
                              });
                            }
                          } else if (!date) {
                            setErrors((prev) => {
                              return [
                                ...prev,
                                {
                                  label: 'deadlineNew',
                                  message: '申込期限を入力してください。',
                                },
                              ];
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                <ErrorMessage
                  $paddingMessage={300}
                  className="xl:ml-[180px] 2xl:ml-[180px]"
                  style={{
                    display: errors.some((e) => e.label === 'deadlineNew') ? 'block' : 'none',
                  }}
                >
                  <span>{errors.find((error) => error.label === 'deadlineNew')?.message}</span>
                </ErrorMessage>
              </div>

              <div className="flex w-full flex-col gap-3 pl-[178px]">
                {scheduleEntries.map((entry, index) => (
                  <div key={index} className="flex flex-col">
                    <div className="flex items-center gap-5">
                      <span className="w-[89px] text-sm">再受講{index + 1}日目</span>
                      <div className="flex items-center gap-2">
                        <DatePicker
                          format="YYYY/MM/DD"
                          getPopupContainer={(trigger) => trigger.parentNode}
                          disabled={!!courseId && form.isHappened}
                          placeholder="yyyy/mm/dd"
                          open={datePickerOpen.id === `retake-${entry.id}` && datePickerOpen.open}
                          onOpenChange={(open) => {
                            handleDatePickerOpen(open ? `retake-${entry.id}` : null, open);
                          }}
                          value={entry.retakeDeadline ? dayjs(entry.retakeDeadline) : ''}
                          onChange={(date) => {
                            updateDeadline(index, date);
                            if (date) {
                              if (dayjs(scheduleEntries[index].date).isBefore(dayjs(date))) {
                                const hasError =
                                  errors.find(
                                    (error) =>
                                      error.label === 'retakeDeadline' && error.id === index,
                                  ) || null;
                                if (!hasError) {
                                  setErrors((prev) => {
                                    return [
                                      ...prev,
                                      {
                                        label: 'retakeDeadline',
                                        id: index,
                                        message: '申込期限を正しく入力してください。',
                                      },
                                    ];
                                  });
                                } else {
                                  setErrors((prev) => {
                                    return prev.map((error) =>
                                      error.label === 'retakeDeadline' && error.id === index
                                        ? {
                                            ...error,
                                            message: '申込期限を正しく入力してください。',
                                          }
                                        : error,
                                    );
                                  });
                                }
                              } else {
                                setErrors((prev) => {
                                  return prev.filter(
                                    (error) =>
                                      !(error.label === 'retakeDeadline' && error.id === index),
                                  );
                                });
                              }
                            } else {
                              const hasError =
                                errors.find(
                                  (error) => error.label === 'retakeDeadline' && error.id === index,
                                ) || null;
                              if (!hasError) {
                                setErrors((prev) => {
                                  return [
                                    ...prev,
                                    {
                                      label: 'retakeDeadline',
                                      id: index,
                                      message: '申込期限を入力してください。',
                                    },
                                  ];
                                });
                              } else {
                                setErrors((prev) => {
                                  return prev.map((error) =>
                                    error.label === 'retakeDeadline' && error.id === index
                                      ? {
                                          ...error,
                                          message: '申込期限を入力してください。',
                                        }
                                      : error,
                                  );
                                });
                              }
                            }
                          }}
                          className={`h-10 w-[345px] text-sm ${errors.filter((e) => e.label === 'retakeDeadline').length > 0 ? 'border-[#ff4d4f] shadow-error' : ''}`}
                        />
                      </div>
                    </div>
                    <ErrorMessage
                      $paddingMessage={119}
                      style={{
                        display: errors.some((e) => e.label === 'retakeDeadline')
                          ? 'block'
                          : 'none',
                      }}
                    >
                      <span>
                        {
                          errors.find(
                            (error) => error.label === 'retakeDeadline' && error.id === index,
                          )?.message
                        }
                      </span>
                    </ErrorMessage>
                  </div>
                ))}
              </div>
            </div>
          </ModalCommon>
        </Spin>
      </div>
    </ModalCourseDetailWrapper>
  );
};

export default ModalCourse;
